import React, { useState } from 'react';
import { Autocomplete, TextField, Typography, CircularProgress, Button } from '@mui/material';
import { useClient } from 'shared/apollo';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import { Heading, RowH, ColH, CloseIcon } from './styles';
import { searchAccounts } from './api';
import UserModal from './UserModal';

type AccountOption = {
  dbId: number;
  name: string;
  type?: string;
  ein?: string;
};

const MAX_RESULTS = 20;

interface NewUserAccountPickerProps {
  open: boolean;
  handleClose: (response?: { success?: boolean }) => void;
}

const NewUserAccountPicker: React.FC<NewUserAccountPickerProps> = ({
  open,
  handleClose,
}) => {
  const client = useClient();
  const [term, setTerm] = useState<string>('');
  const [options, setOptions] = useState<AccountOption[]>([]);
  const [account, setAccount] = useState<AccountOption | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [userModalOpen, setUserModalOpen] = useState<boolean>(false);

  const runSearch = async () => {
    const search = term.trim();
    if (search.length < 2) {
      setError('Type at least 2 characters of the account name or EIN');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const found = await searchAccounts(client!, search, MAX_RESULTS);
      const accounts: AccountOption[] = Array.isArray(found) ? found : [];
      setOptions(accounts.slice(0, MAX_RESULTS));
      if (accounts.length === 0) setError('No accounts matched that search');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setError(err?.message || 'Unable to search accounts');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCloseUserModal = (newUser?: any) => {
    setUserModalOpen(false);
    if (newUser?.firstName) {
      handleClose({ success: true });
      return;
    }
    setAccount(null);
  };

  return (
    <TrueImpactModal
      open={open}
      hideHeader={true}
      hideFooter={true}
      maxWidth="700px"
      onClose={() => handleClose()}
    >
      <Heading>
        <CloseIcon onClick={() => handleClose()} />
        <RowH>
          <ColH>
            <Typography variant="h2">Add new user</Typography>
            <br />
            <Typography variant="body1">
              Every user belongs to an account. Search the account by name or
              EIN, press Enter, then pick one of the matches (up to{' '}
              {MAX_RESULTS}).
            </Typography>
          </ColH>
        </RowH>
      </Heading>
      <ColH style={{ padding: '24px' }}>
        <Autocomplete
          options={options}
          value={account}
          filterOptions={x => x}
          getOptionLabel={option =>
            `${option.name} (ID: ${option.dbId}${
              option.ein ? `, EIN: ${option.ein}` : ''
            })`
          }
          isOptionEqualToValue={(option, value) => option.dbId === value.dbId}
          onChange={(_, value) => setAccount(value)}
          noOptionsText="Press Enter to search"
          loading={loading}
          renderInput={params => (
            <TextField
              {...params}
              placeholder="Account name or EIN"
              onChange={e => setTerm(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  runSearch();
                }
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {error && (
          <RowH style={{ marginTop: '16px' }}>
            <Typography style={{ color: '#ff7272' }} variant="body2">
              {error}
            </Typography>
          </RowH>
        )}
        {account && (
          <RowH
            style={{
              marginTop: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="body2">
              Creating a user for <b>{account.name}</b> (ID: {account.dbId})
            </Typography>
            <Button variant="outlined" onClick={() => setUserModalOpen(true)}>
              Continue
            </Button>
          </RowH>
        )}
      </ColH>
      {account && userModalOpen && (
        <UserModal
          open={userModalOpen}
          setOpen={setUserModalOpen}
          user={undefined}
          handleClose={handleCloseUserModal}
          accountId={account.dbId}
        />
      )}
    </TrueImpactModal>
  );
};

export default NewUserAccountPicker;
